// Redux
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Http client
import { httpClient } from "@dbox/core/httpClient";

// Constants
import { STATUS, THUNK_STATUS } from "@dbox/components/constants";

const initialState = {
  toc: [],
  validateReport: {
    data: null,
    error: null,
    success: false,
    state: THUNK_STATUS.idle,
  },
  viewReport: {
    data: null,
    error: null,
    success: false,
    isLoading: true,
    state: THUNK_STATUS.idle,
  },
  allReports: {
    data: null,
    error: null,
    success: false,
    state: THUNK_STATUS.idle,
  },
  generateReport: {
    id: "",
    year: "",
    data: null,
    error: null,
    success: false,
    isLoading: true,
    navigation: false,
    templateVersion: "",
    state: THUNK_STATUS.idle,
  },
  enumsReportsYears: {
    data: null,
    error: null,
    success: false,
    state: THUNK_STATUS.idle,
  },
  enumsReportsTypes: {
    data: null,
    error: null,
    success: false,
    state: THUNK_STATUS.idle,
  },
  enumsCalctoolsAttrValues: {
    data: null,
    error: null,
    success: false,
    state: THUNK_STATUS.idle,
  },
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setReportId: (state, action) => {
      state.generateReport.id = action.payload;
    },
    resetReportId: (state) => {
      state.generateReport.id = "";
    },
    setReportYear: (state, action) => {
      state.generateReport.year = action.payload;
    },
    resetReportYear: (state) => {
      state.generateReport.year = "";
    },
    setReportTemplateVersion: (state, action) => {
      state.generateReport.templateVersion = action.payload;
    },
    resetReportTemplateVersion: (state) => {
      state.generateReport.templateVersion = "";
    },
    setReportNavigation: (state, action) => {
      state.generateReport.navigation = action.payload;
    },
    resetReportNavigation: (state) => {
      state.generateReport.navigation = false;
    },
    resetGenerateReport: (state) => {
      state.generateReport = initialState.generateReport;
    },
    resetViewReport: (state) => {
      state.viewReport = initialState.viewReport;
    },
    setReportToC: (state, action) => {
      state.toc = action.payload;
    },
    resetReportToC: (state) => {
      state.toc = initialState.toc;
    },
    resetValidateReport: (state) => {
      state.toc = initialState.validateReport;
    },
  },
  extraReducers: (builder) => {
    builder
      // ***** GENERATE REPORTS *****
      .addCase(fetchReportsGenerateByReportIdAndYear.pending, (state, action) => {
        if (state.generateReport.state === THUNK_STATUS.idle) {
          state.generateReport.error = null;
          state.generateReport.isLoading = true;
          state.generateReport.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchReportsGenerateByReportIdAndYear.fulfilled, (state, action) => {
        if (state.generateReport.state === THUNK_STATUS.pending) {
          state.generateReport.success = true;
          state.generateReport.isLoading = false;
          state.generateReport.state = THUNK_STATUS.idle;
          state.generateReport.data = action.payload;
        }
      })
      .addCase(fetchReportsGenerateByReportIdAndYear.rejected, (state, action) => {
        if (state.generateReport.state === THUNK_STATUS.pending) {
          state.generateReport.error = action.error;
          state.generateReport.isLoading = false;
          state.generateReport.state = THUNK_STATUS.idle;
        }
      })
      // ***** ENUMS REPORTS TYPES *****
      .addCase(fetchEnumsReportsTypes.pending, (state, action) => {
        if (state.enumsReportsTypes.state === THUNK_STATUS.idle) {
          state.enumsReportsTypes.error = null;
          state.enumsReportsTypes.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchEnumsReportsTypes.fulfilled, (state, action) => {
        if (state.enumsReportsTypes.state === THUNK_STATUS.pending) {
          state.enumsReportsTypes.success = true;
          state.enumsReportsTypes.state = THUNK_STATUS.idle;
          state.enumsReportsTypes.data = action.payload.types;
        }
      })
      .addCase(fetchEnumsReportsTypes.rejected, (state, action) => {
        if (state.enumsReportsTypes.state === THUNK_STATUS.pending) {
          state.enumsReportsTypes.error = action.error;
          state.enumsReportsTypes.state = THUNK_STATUS.idle;
        }
      })
      // ***** FETCH ALL REPORTS *****
      .addCase(fetchAllReports.pending, (state, action) => {
        if (state.allReports.state === THUNK_STATUS.idle) {
          state.allReports.error = null;
          state.allReports.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchAllReports.fulfilled, (state, action) => {
        if (state.allReports.state === THUNK_STATUS.pending) {
          state.allReports.success = true;
          state.allReports.state = THUNK_STATUS.idle;
          state.allReports.data = action.payload.reports_connections;
        }
      })
      .addCase(fetchAllReports.rejected, (state, action) => {
        if (state.allReports.state === THUNK_STATUS.pending) {
          state.allReports.error = action.error;
          state.allReports.state = THUNK_STATUS.idle;
        }
      })
      // ***** FETCH REPORT BY ID *****
      .addCase(fetchReportsById.pending, (state, action) => {
        if (state.viewReport.state === THUNK_STATUS.idle) {
          state.viewReport.error = null;
          state.viewReport.isLoading = true;
          state.viewReport.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchReportsById.fulfilled, (state, action) => {
        if (state.viewReport.state === THUNK_STATUS.pending) {
          state.viewReport.success = true;
          state.viewReport.isLoading = false;
          state.viewReport.data = action.payload;
          state.viewReport.state = THUNK_STATUS.idle;
        }
      })
      .addCase(fetchReportsById.rejected, (state, action) => {
        if (state.viewReport.state === THUNK_STATUS.pending) {
          state.viewReport.error = action.error;
          state.viewReport.isLoading = false;
          state.viewReport.state = THUNK_STATUS.idle;
        }
      })
      // ***** FETCH ENUMS REPORT YEARS *****
      .addCase(fetchEnumsReportsYears.pending, (state, action) => {
        if (state.enumsReportsYears.state === THUNK_STATUS.idle) {
          state.enumsReportsYears.error = null;
          state.enumsReportsYears.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchEnumsReportsYears.fulfilled, (state, action) => {
        if (state.enumsReportsYears.state === THUNK_STATUS.pending) {
          state.enumsReportsYears.success = true;
          state.enumsReportsYears.data = action.payload.data;
          state.enumsReportsYears.state = THUNK_STATUS.idle;
        }
      })
      .addCase(fetchEnumsReportsYears.rejected, (state, action) => {
        if (state.enumsReportsYears.state === THUNK_STATUS.pending) {
          state.enumsReportsYears.error = action.error;
          state.enumsReportsYears.state = THUNK_STATUS.idle;
        }
      })
      // ***** FETCH VALIDATE REPORT *****
      .addCase(fetchReportValidate.pending, (state, action) => {
        if (state.validateReport.state === THUNK_STATUS.idle) {
          state.validateReport.error = null;
          state.validateReport.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchReportValidate.fulfilled, (state, action) => {
        if (state.validateReport.state === THUNK_STATUS.pending) {
          state.validateReport.success = true;
          state.validateReport.state = THUNK_STATUS.idle;
          state.validateReport.data = action.payload.report_data;
        }
      })
      .addCase(fetchReportValidate.rejected, (state, action) => {
        if (state.validateReport.state === THUNK_STATUS.pending) {
          state.validateReport.error = action.error;
          state.validateReport.state = THUNK_STATUS.idle;
        }
      })
      // ***** FETCH ENUMS CALC. TOOLS ATTR. VALUES *****
      .addCase(fetchEnumCalculationtoolsAttrValues.pending, (state, action) => {
        if (state.enumsCalctoolsAttrValues.state === THUNK_STATUS.idle) {
          state.enumsCalctoolsAttrValues.error = null;
          state.enumsCalctoolsAttrValues.state = THUNK_STATUS.pending;
        }
      })
      .addCase(fetchEnumCalculationtoolsAttrValues.fulfilled, (state, action) => {
        if (state.enumsCalctoolsAttrValues.state === THUNK_STATUS.pending) {
          state.enumsCalctoolsAttrValues.success = true;
          state.enumsCalctoolsAttrValues.state = THUNK_STATUS.idle;
          state.enumsCalctoolsAttrValues.data = action.payload.types;
        }
      })
      .addCase(fetchEnumCalculationtoolsAttrValues.rejected, (state, action) => {
        if (state.enumsCalctoolsAttrValues.state === THUNK_STATUS.pending) {
          state.enumsCalctoolsAttrValues.error = action.error;
          state.enumsCalctoolsAttrValues.state = THUNK_STATUS.idle;
        }
      });
  },
});

export const { setReportId } = reportsSlice.actions;
export const { setReportToC } = reportsSlice.actions;
export const { setReportYear } = reportsSlice.actions;
export const { setReportNavigation } = reportsSlice.actions;
export const { setReportTemplateVersion } = reportsSlice.actions;

export const { resetReportId } = reportsSlice.actions;
export const { resetReportToC } = reportsSlice.actions;
export const { resetReportYear } = reportsSlice.actions;
export const { resetViewReport } = reportsSlice.actions;
export const { resetValidateReport } = reportsSlice.actions;
export const { resetGenerateReport } = reportsSlice.actions;
export const { resetReportNavigation } = reportsSlice.actions;
export const { resetReportTemplateVersion } = reportsSlice.actions;

export const fetchEnumsReportsTypes = createAsyncThunk(
  "reports/fetchEnumsReportsTypes",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpClient.get("enums/reports/types");

      return response?.data;
    } catch (error) {
      if (error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchEnumCalculationtoolsAttrValues = createAsyncThunk(
  "reports/fetchEnumCalculationtoolsAttrValues",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpClient.get("enums/calculationtools/attributes/values");

      return response?.data;
    } catch (error) {
      if (error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchEnumsReportsYears = createAsyncThunk(
  "reports/fetchEnumsReportsYears",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpClient.get("reports-management/enums/years");

      return response?.data;
    } catch (error) {
      if (error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchReportsById = createAsyncThunk("reports/fetchReportsById", async (payload, { rejectWithValue }) => {
  try {
    const response = await httpClient.get(`reports-management/${payload?.id}`);

    return response?.data;
  } catch (error) {
    if (error.response.data.message) {
      return rejectWithValue(error.response.data.message);
    } else {
      return rejectWithValue(error.message);
    }
  }
});

export const fetchReportsGenerateByReportIdAndYear = createAsyncThunk(
  "reports/fetchReportsGenerateByReportIdAndYear",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpClient.get(`/reports-management/generate/${payload?.reportId}/year/${payload?.year}`);

      return response?.data;
    } catch (error) {
      if (error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchAllReports = createAsyncThunk("reports/fetchAllReports", async (payload, { rejectWithValue }) => {
  try {
    const response = await httpClient.get("/reports-management", {
      params: {
        paginate: false,
        status: STATUS.enabled,
        date_to: payload?.filter?.dateTo,
        date_from: payload?.filter?.dateFrom,
      },
    });

    return response?.data;
  } catch (error) {
    if (error.response.data.message) {
      return rejectWithValue(error.response.data.message);
    } else {
      return rejectWithValue(error.message);
    }
  }
});

export const postReportsManagement = createAsyncThunk(
  "reports/postReportsManagement",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpClient.post("/reports-management", {
        report_data: payload,
      });

      return response?.data;
    } catch (error) {
      if (error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const fetchReportValidate = createAsyncThunk(
  "reports/fetchReportValidate",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await httpClient.get(
        `/reports-management/generate/${payload?.id}/year/${payload?.year}/validate`
      );

      return response?.data;
    } catch (error) {
      if (error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export default reportsSlice;
